import { useState } from "react";
import { Link } from "react-router-dom";
import { API, formatApiErrorDetail } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { MapPin, ArrowLeft, Mail } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useBranding } from "../contexts/BrandingContext";

export default function ForgotPasswordPage() {
  const { t, i18n } = useTranslation();
  const { branding } = useBranding();
  const brandName = (i18n.language === "el" && branding?.brand_name_el) || branding?.brand_name || "TableCy";
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="w-full max-w-md space-y-8">
        {/* Logo (branding-driven) */}
        <Link to="/" className="flex items-center gap-2 text-primary">
          {branding?.use_image && branding?.logo_url ? (
            <img src={branding.logo_url} alt={brandName} className="h-8 w-8 object-contain" />
          ) : branding?.logo_svg ? (
            <span className="h-8 w-8 inline-flex items-center justify-center" dangerouslySetInnerHTML={{ __html: branding.logo_svg }} />
          ) : (
            <MapPin className="h-8 w-8" />
          )}
          <span className="text-2xl font-semibold">{brandName}</span>
        </Link>

        <Link to="/login" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" />
          {t("forgot:back_login")}
        </Link>

        {success ? (
          <div className="space-y-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-medium text-foreground">{t("forgot:title")}</h1>
              <p className="mt-2 text-muted-foreground">
                {t("forgot:sent")}
              </p>
            </div>
            <Button asChild className="w-full">
              <Link to="/login">{t("forgot:back_login")}</Link>
            </Button>
          </div>
        ) : (
          <>
            <div>
              <h1 className="text-3xl font-medium text-foreground">{t("forgot:title")}</h1>
              <p className="mt-2 text-muted-foreground">
                {t("forgot:subtitle")}
              </p>
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm" data-testid="forgot-password-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">{t("forgot:email")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-password-email-input"
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-password-submit-button">
                {loading ? t("common:loading") : t("forgot:submit")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
